import { unzipSync } from "fflate";
import { jsPDF } from "jspdf";

const MAX_SOURCE_BYTES = 15 * 1024 * 1024;
const MAX_VISIBLE_SHEETS = 10;
const MAX_ROWS_PER_SHEET = 500;
const MAX_COLUMNS_PER_SHEET = 50;
const MAX_NONEMPTY_CELLS = 5_000;
const MAX_CELL_TEXT_CHARS = 2_000;
const MAX_ZIP_ENTRIES = 2_000;
const MAX_UNCOMPRESSED_BYTES = 50 * 1024 * 1024;
const MAX_COMPRESSION_RATIO = 200;
const MAX_WRAPPED_LINES_PER_CELL = 40;

export interface ConvertedSpreadsheetPdf {
  fileData: ArrayBuffer;
  filename: string;
  sheetCount: number;
}

interface WorkbookSheet {
  name: string;
  path: string;
}

function exactArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function convertedFilename(sourceFilename: string): string {
  const base = sourceFilename.trim().replace(/\.(xlsx|xlsm)$/i, "");
  return `${base || "spreadsheet"}-converted.pdf`;
}

function assertSafeOoxmlArchive(source: ArrayBuffer): void {
  const bytes = new Uint8Array(source);
  const view = new DataView(source);
  const minimumEocdOffset = Math.max(0, bytes.byteLength - 65_557);
  let eocdOffset = -1;
  for (let offset = bytes.byteLength - 22; offset >= minimumEocdOffset; offset -= 1) {
    if (view.getUint32(offset, true) === 0x06054b50) {
      eocdOffset = offset;
      break;
    }
  }
  if (eocdOffset < 0) throw new Error("The workbook ZIP directory is invalid");
  const entryCount = view.getUint16(eocdOffset + 10, true);
  const centralDirectoryOffset = view.getUint32(eocdOffset + 16, true);
  if (entryCount <= 0 || entryCount > MAX_ZIP_ENTRIES) {
    throw new Error("The workbook contains too many internal files");
  }

  let offset = centralDirectoryOffset;
  let totalUncompressedBytes = 0;
  let hasContentTypes = false;
  let hasWorkbook = false;
  const decoder = new TextDecoder();
  for (let index = 0; index < entryCount; index += 1) {
    if (offset + 46 > bytes.byteLength || view.getUint32(offset, true) !== 0x02014b50) {
      throw new Error("The workbook ZIP directory is invalid");
    }
    const flags = view.getUint16(offset + 8, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const uncompressedSize = view.getUint32(offset + 24, true);
    const filenameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const nextOffset = offset + 46 + filenameLength + extraLength + commentLength;
    if (
      (flags & 0x1) !== 0 ||
      compressedSize === 0xffffffff ||
      uncompressedSize === 0xffffffff ||
      nextOffset > bytes.byteLength
    ) {
      throw new Error("Encrypted or ZIP64 workbooks cannot be converted");
    }
    totalUncompressedBytes += uncompressedSize;
    if (totalUncompressedBytes > MAX_UNCOMPRESSED_BYTES) {
      throw new Error("The expanded workbook exceeds the 50 MB safety limit");
    }
    if (
      uncompressedSize > 0 &&
      compressedSize > 0 &&
      uncompressedSize / compressedSize > MAX_COMPRESSION_RATIO
    ) {
      throw new Error("The workbook compression ratio exceeds the safety limit");
    }
    const filename = decoder.decode(
      bytes.subarray(offset + 46, offset + 46 + filenameLength),
    );
    if (filename === "[Content_Types].xml") hasContentTypes = true;
    if (filename === "xl/workbook.xml") hasWorkbook = true;
    offset = nextOffset;
  }
  if (!hasContentTypes || !hasWorkbook) {
    throw new Error("The file is not a valid Excel workbook");
  }
}

function parseXml(bytes: Uint8Array | undefined, label: string): XMLDocument {
  if (!bytes) throw new Error(`The workbook is missing ${label}`);
  const document = new DOMParser().parseFromString(
    new TextDecoder().decode(bytes),
    "application/xml",
  );
  if (document.getElementsByTagName("parsererror").length > 0) {
    throw new Error(`Cherry could not read ${label}`);
  }
  return document;
}

function normalizeArchivePath(path: string): string {
  const parts: string[] = [];
  for (const part of path.replace(/^\//, "").split("/")) {
    if (!part || part === ".") continue;
    if (part === "..") parts.pop();
    else parts.push(part);
  }
  return parts.join("/");
}

function workbookSheets(files: Record<string, Uint8Array>): WorkbookSheet[] {
  const workbook = parseXml(files["xl/workbook.xml"], "the workbook index");
  const relationships = parseXml(
    files["xl/_rels/workbook.xml.rels"],
    "the workbook relationships",
  );
  const targets = new Map<string, string>();
  for (const relationship of Array.from(
    relationships.getElementsByTagName("Relationship"),
  )) {
    const id = relationship.getAttribute("Id");
    const target = relationship.getAttribute("Target");
    const targetMode = relationship.getAttribute("TargetMode");
    if (id && target && targetMode !== "External") {
      targets.set(
        id,
        normalizeArchivePath(target.startsWith("/") ? target : `xl/${target}`),
      );
    }
  }
  const sheets: WorkbookSheet[] = [];
  for (const sheet of Array.from(workbook.getElementsByTagName("sheet"))) {
    if ((sheet.getAttribute("state") ?? "visible") !== "visible") continue;
    const id = sheet.getAttribute("r:id");
    const name = sheet.getAttribute("name")?.trim();
    const path = id ? targets.get(id) : undefined;
    if (!name || !path) throw new Error("A visible workbook sheet is invalid");
    sheets.push({ name, path });
  }
  return sheets;
}

function sharedStrings(files: Record<string, Uint8Array>): string[] {
  const bytes = files["xl/sharedStrings.xml"];
  if (!bytes) return [];
  const document = parseXml(bytes, "shared spreadsheet text");
  return Array.from(document.getElementsByTagName("si"), (item) =>
    Array.from(item.getElementsByTagName("t"), (text) => text.textContent ?? "").join(""),
  );
}

function dateStyleIndexes(files: Record<string, Uint8Array>): Set<number> {
  const bytes = files["xl/styles.xml"];
  if (!bytes) return new Set();
  const document = parseXml(bytes, "spreadsheet styles");
  const customDateFormats = new Set<number>();
  const numFmts = document.getElementsByTagName("numFmts")[0];
  if (numFmts) {
    for (const numFmt of Array.from(numFmts.getElementsByTagName("numFmt"))) {
      const id = Number(numFmt.getAttribute("numFmtId"));
      const code = (numFmt.getAttribute("formatCode") ?? "")
        .replace(/"[^"]*"/g, "")
        .replace(/\[[^\]]*\]/g, "");
      if (Number.isInteger(id) && /[ymdhis]/i.test(code)) customDateFormats.add(id);
    }
  }
  const builtInDateFormats = new Set([
    14, 15, 16, 17, 18, 19, 20, 21, 22, 27, 30, 36, 45, 46, 47, 50, 57,
  ]);
  const dateStyles = new Set<number>();
  const cellXfs = document.getElementsByTagName("cellXfs")[0];
  if (!cellXfs) return dateStyles;
  Array.from(cellXfs.getElementsByTagName("xf")).forEach((xf, index) => {
    const numFmtId = Number(xf.getAttribute("numFmtId"));
    if (builtInDateFormats.has(numFmtId) || customDateFormats.has(numFmtId)) {
      dateStyles.add(index);
    }
  });
  return dateStyles;
}

function excelSerialToDisplay(value: number): string {
  const milliseconds = Math.round(value * 86_400_000);
  const date = new Date(Date.UTC(1899, 11, 30) + milliseconds);
  if (!Number.isFinite(date.getTime())) return String(value);
  const iso = date.toISOString();
  return value % 1 === 0 ? iso.slice(0, 10) : iso.slice(0, 16).replace("T", " ");
}

function columnIndex(reference: string): number {
  const letters = reference.match(/^[A-Z]+/i)?.[0]?.toUpperCase();
  if (!letters) return -1;
  let result = 0;
  for (const letter of letters) result = result * 26 + letter.charCodeAt(0) - 64;
  return result - 1;
}

function cellText(
  cell: Element,
  strings: string[],
  dateStyles: Set<number>,
): string {
  const type = cell.getAttribute("t") ?? "n";
  if (type === "inlineStr") {
    return Array.from(cell.getElementsByTagName("t"), (item) => item.textContent ?? "").join("");
  }
  const raw = cell.getElementsByTagName("v")[0]?.textContent ?? "";
  if (type === "s") return strings[Number(raw)] ?? "";
  if (type === "b") return raw === "1" ? "TRUE" : "FALSE";
  if (type === "d") return raw.replace("T", " ").replace(/Z$/, "");
  const styleIndex = Number(cell.getAttribute("s"));
  const numeric = Number(raw);
  if (raw && dateStyles.has(styleIndex) && Number.isFinite(numeric)) {
    return excelSerialToDisplay(numeric);
  }
  return raw;
}

function sheetRows(
  files: Record<string, Uint8Array>,
  sheet: WorkbookSheet,
  strings: string[],
  dateStyles: Set<number>,
): string[][] {
  const document = parseXml(files[sheet.path], sheet.name);
  const rows: string[][] = [];
  let nonemptyCells = 0;
  for (const row of Array.from(document.getElementsByTagName("row"))) {
    const values: string[] = [];
    for (const cell of Array.from(row.getElementsByTagName("c"))) {
      const index = columnIndex(cell.getAttribute("r") ?? "");
      if (index < 0) continue;
      if (index >= MAX_COLUMNS_PER_SHEET) {
        throw new Error(
          `${sheet.name} has more than ${MAX_COLUMNS_PER_SHEET} populated columns`,
        );
      }
      const text = cellText(cell, strings, dateStyles).trim();
      if (text.length > MAX_CELL_TEXT_CHARS) {
        throw new Error(
          `${sheet.name} contains a cell longer than ${MAX_CELL_TEXT_CHARS.toLocaleString()} characters`,
        );
      }
      values[index] = text;
      if (text) nonemptyCells += 1;
    }
    while (values.length > 0 && !values[values.length - 1]) values.pop();
    if (values.some(Boolean)) rows.push(values);
    if (rows.length > MAX_ROWS_PER_SHEET) {
      throw new Error(`${sheet.name} has more than ${MAX_ROWS_PER_SHEET} populated rows`);
    }
  }
  if (nonemptyCells > MAX_NONEMPTY_CELLS) {
    throw new Error(
      `The workbook has more than ${MAX_NONEMPTY_CELLS.toLocaleString()} populated cells`,
    );
  }
  return rows;
}

function drawSheet(
  pdf: jsPDF,
  sheetName: string,
  rows: string[][],
  startOnNewPage: boolean,
): void {
  if (startOnNewPage) pdf.addPage("letter", "landscape");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 24;
  const footerHeight = 20;
  const usableWidth = pageWidth - margin * 2;
  const columnCount = Math.max(1, ...rows.map((row) => row.length));
  const columnWidth = usableWidth / columnCount;
  const fontSize = columnCount > 20 ? 5 : columnCount > 12 ? 6 : 8;
  const lineHeight = fontSize + 2;
  const minimumRowHeight = lineHeight + 4;
  let y = margin;

  const drawFooter = () => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6);
    pdf.setTextColor(90);
    pdf.text(
      "Converted by Cherry. Workbook formulas and macros were not executed; cached values are shown.",
      margin,
      pageHeight - 8,
    );
    pdf.setTextColor(0);
  };
  const drawTitle = () => {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text(sheetName, margin, y);
    y += 14;
  };

  drawTitle();
  if (rows.length === 0) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.text("No visible cell values", margin, y);
    drawFooter();
    return;
  }
  for (const [rowIndex, row] of rows.entries()) {
    pdf.setFont("helvetica", rowIndex === 0 ? "bold" : "normal");
    pdf.setFontSize(fontSize);
    const wrapped = Array.from({ length: columnCount }, (_, column) =>
      pdf.splitTextToSize(row[column] ?? "", Math.max(8, columnWidth - 4)),
    );
    const maxLines = Math.max(1, ...wrapped.map((lines) => lines.length));
    if (maxLines > MAX_WRAPPED_LINES_PER_CELL) {
      throw new Error(`${sheetName} contains a cell too long for the PDF layout`);
    }
    const rowHeight = Math.max(minimumRowHeight, maxLines * lineHeight + 4);
    if (y + rowHeight > pageHeight - footerHeight) {
      drawFooter();
      pdf.addPage("letter", "landscape");
      y = margin;
      drawTitle();
    }
    for (let column = 0; column < columnCount; column += 1) {
      const x = margin + column * columnWidth;
      pdf.setDrawColor(190);
      pdf.rect(x, y, columnWidth, rowHeight);
      pdf.text(wrapped[column] ?? [""], x + 2, y + lineHeight);
    }
    y += rowHeight;
  }
  drawFooter();
}

/**
 * Reads only visible OOXML sheet XML and cached formula values. VBA projects,
 * external links, formulas, and other active workbook content are never loaded
 * or executed.
 */
export async function convertSpreadsheetToPdf(
  source: ArrayBuffer,
  sourceFilename: string,
): Promise<ConvertedSpreadsheetPdf> {
  if (source.byteLength <= 0 || source.byteLength > MAX_SOURCE_BYTES) {
    throw new Error("The spreadsheet is empty or exceeds the 15 MB limit");
  }
  const signature = new Uint8Array(source, 0, Math.min(4, source.byteLength));
  if (
    signature.length < 4 ||
    signature[0] !== 0x50 ||
    signature[1] !== 0x4b ||
    signature[2] !== 0x03 ||
    signature[3] !== 0x04
  ) {
    throw new Error("Only valid XLSX or XLSM workbooks can be converted");
  }
  assertSafeOoxmlArchive(source);
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(new Uint8Array(source));
  } catch {
    throw new Error("Cherry could not read this spreadsheet");
  }
  const sheets = workbookSheets(files);
  if (sheets.length === 0) throw new Error("The workbook has no visible sheets");
  if (sheets.length > MAX_VISIBLE_SHEETS) {
    throw new Error(`The workbook has more than ${MAX_VISIBLE_SHEETS} visible sheets`);
  }
  const strings = sharedStrings(files);
  const dateStyles = dateStyleIndexes(files);
  let totalNonemptyCells = 0;
  const renderedSheets = sheets.map((sheet) => {
    const rows = sheetRows(files, sheet, strings, dateStyles);
    totalNonemptyCells += rows.reduce(
      (total, row) => total + row.filter(Boolean).length,
      0,
    );
    return { name: sheet.name, rows };
  });
  if (totalNonemptyCells > MAX_NONEMPTY_CELLS) {
    throw new Error(
      `The workbook has more than ${MAX_NONEMPTY_CELLS.toLocaleString()} populated cells`,
    );
  }

  const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  for (const [index, sheet] of renderedSheets.entries()) {
    drawSheet(pdf, sheet.name, sheet.rows, index > 0);
  }
  const fileData = pdf.output("arraybuffer");
  if (fileData.byteLength > MAX_SOURCE_BYTES) {
    throw new Error("The converted PDF exceeds the 15 MB limit");
  }
  return {
    fileData: exactArrayBuffer(new Uint8Array(fileData)),
    filename: convertedFilename(sourceFilename),
    sheetCount: renderedSheets.length,
  };
}
